/** Small DOM helpers shared by the screens. */

export interface MountedScreen {
  /** Receives focus when the screen appears (an h2 with tabindex="-1"). */
  readonly heading: HTMLElement;
  dispose(): void;
}

export function requireElement<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`카톡 성적표 화면 요소를 찾지 못했어요: ${selector}`);
  return element;
}

export function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Moves focus to a new screen's heading and brings the top of that screen into view. */
export function focusScreenHeading(heading: HTMLElement): void {
  heading.focus({ preventScroll: true });
  const target = heading.closest<HTMLElement>('.krc-screen') ?? heading;
  const top = target.getBoundingClientRect().top;
  if (top < 0 || top > window.innerHeight * 0.4) {
    target.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }
}

/** Clipboard write with a selection-based fallback for older in-app browsers. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path.
  }
  try {
    const input = document.createElement('textarea');
    input.value = text;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.top = '0';
    input.style.opacity = '0';
    document.body.append(input);
    input.select();
    input.setSelectionRange(0, text.length);
    const copied = document.execCommand('copy');
    input.remove();
    return copied;
  } catch {
    return false;
  }
}

/** Selects an element's text so the user can copy it by hand. */
export function selectElementText(element: HTMLElement): void {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.selectNodeContents(element);
  selection.removeAllRanges();
  selection.addRange(range);
}

export function downloadFile(file: File): void {
  const url = URL.createObjectURL(file);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.name;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}
